import { LIST_GC_TIME_MS, LIST_STALE_TIME_MS, useApiClient, useUtcToday } from '@klareospace/pharos-lib-front-utils';
import type { Kpi, KpiKey } from '@klareospace/pharos-lib-types';
import { KPI_GRANULARITY } from '@klareospace/pharos-lib-types';
import { useQuery } from '@tanstack/react-query';

import { useMasterContext } from '&/master';

import type { GetKpisMtdParams, KpisApi } from '../api';
import { createKpisApi, kpiKeys } from '../api';
import { resolveKpiPendingRefetchInterval } from './resolve-kpi-refetch-interval';
import { useIsKpiReadEnabled } from './use-kpi-read-access';
import { useKpiRealtimeSync } from './use-kpi-realtime-sync';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseKpiMtdQueryParams {
  /** The "This month" tab must be active for this query to run — no request fires on page load. */
  enabled: boolean;
  fetchKpi: (api: KpisApi, params: GetKpisMtdParams) => Promise<Kpi>;
  key: KpiKey;
}

export interface UseKpiMtdQueryResult {
  data: Kpi | undefined;
  isError: boolean;
  isLoading: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Shared mtd-granularity query + realtime sync for `order-kpis`/`driver-task-kpis`, gated on `feat-dashboard` + `kpis:read` + `enabled`. */
export function useKpiMtdQuery({ enabled, fetchKpi, key }: UseKpiMtdQueryParams): UseKpiMtdQueryResult {
  const client = useApiClient('deliveries');
  const api = createKpisApi(client);
  const { siteId, spaceId } = useMasterContext();
  const isReadEnabled = useIsKpiReadEnabled();
  const isQueryEnabled = enabled && isReadEnabled;
  const utcNow = useUtcToday();
  const year = utcNow.getUTCFullYear();
  const month = utcNow.getUTCMonth() + 1;
  const scope = { siteId: siteId ?? undefined, spaceId: spaceId ?? undefined };
  const params = { month, year, ...scope };
  const queryKey = kpiKeys.mtd(key, params);

  const query = useQuery({
    enabled: isQueryEnabled,
    gcTime: LIST_GC_TIME_MS,
    queryFn: (): Promise<Kpi> => fetchKpi(api, params),
    queryKey,
    refetchInterval: resolveKpiPendingRefetchInterval,
    staleTime: LIST_STALE_TIME_MS,
  });

  useKpiRealtimeSync({
    bound: { month, year },
    enabled: isQueryEnabled,
    granularity: KPI_GRANULARITY.MONTH_TO_DATE,
    kpiKey: key,
    queryKey,
    scope,
    signalPresence: false,
  });

  return { data: query.data, isError: query.isError, isLoading: query.isLoading };
}
